import copy from 'clipboard-copy';
import { ClipboardCopy, FileDown } from 'lucide-react';
import { ReactNode } from 'react';

import { useNotifications } from '@/components/ui/notifications';
import { cn } from '@/utils/cn';

import { MouseOverMenu } from './mouse-over-menu';

type TextExportMenuProps = {
  text: string;
  children: ReactNode;
  toClipboard?: boolean;
  toFile?: boolean;
  fileOptions?: { type: string; name: () => string };
  className?: string;
};

const TextExportMenu = ({
  text,
  children,
  toClipboard,
  toFile,
  fileOptions,
  className,
}: TextExportMenuProps) => {
  const { addNotification } = useNotifications();

  const onCopyToClipboard = () => {
    copy(text)
      .then(() => {
        addNotification({
          type: 'info',
          title: 'Copied to the clipboard!',
          timestamp: new Date(),
          duration: 10000,
        });
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: 'Failed to copy to the clipboard!',
          timestamp: new Date(),
          duration: 10000,
        });
      });
  };

  const onSaveAs = () => {
    const blob = new Blob([text], {
      type: fileOptions ? fileOptions.type : 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    document.body.appendChild(anchor);
    anchor.download = fileOptions ? fileOptions.name() : 'sample.txt';
    anchor.href = url;
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const buttonCn = cn(
    'w-28',
    'px-3',
    'py-1',
    'rounded-sm',
    ['inline-flex', 'flex-row', 'justify-start', 'items-center', 'gap-x-1'],
    ['text-base', 'font-sans'],
    ['focus:ring-2'],
  );

  return (
    <MouseOverMenu className={className}>
      <MouseOverMenu.Content>{children}</MouseOverMenu.Content>

      <MouseOverMenu.Menu>
        {toClipboard && (
          <button
            className={cn(buttonCn, [
              'text-white',
              'bg-blue-600',
              'hover:bg-blue-500',
            ])}
            onClick={() => onCopyToClipboard()}
          >
            <ClipboardCopy size={18} />
            Copy
          </button>
        )}

        {toFile && (
          <button
            className={cn(buttonCn, [
              'text-white',
              'bg-violet-600',
              'hover:bg-violet-500',
            ])}
            onClick={() => onSaveAs()}
          >
            <FileDown size={18} />
            Save As
          </button>
        )}
      </MouseOverMenu.Menu>
    </MouseOverMenu>
  );
};

TextExportMenu.displayName = 'TextExportMenu';

export { TextExportMenu };
